import create from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  SvgCanvasData,
  SvgCanvasElement,
  SvgNodeElement,
  SelectedState,
  SvgAsset,
  SvgAiHistory,
  SvgAnimationProps,
  SvgSubElement,
} from '../types/svgTypes';
import svgAPIService from '../services/svgApi';
import { parseSvgContent, rebuildSvgContent } from '../utils/svgParser';
import { extractAnimationFromSvg } from '../utils/svgAnimationUtils';

// 本地素材缓存 key
const LOCAL_ASSET_STORAGE_KEY = 'ai_svg_assets_local';

const loadLocalAssetsSafely = (): SvgAsset[] => {
  try {
    if (typeof window === 'undefined') {
      return [];
    }
    const raw = window.localStorage.getItem(LOCAL_ASSET_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed as SvgAsset[];
  } catch (error) {
    console.error('从 localStorage 读取 SVG 素材失败:', error);
    return [];
  }
};

const saveLocalAssetsSafely = (assets: SvgAsset[]): void => {
  try {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(
      LOCAL_ASSET_STORAGE_KEY,
      JSON.stringify(assets),
    );
  } catch (error) {
    console.error('写入 localStorage SVG 素材失败:', error);
  }
};

interface SvgStoreState {
  canvas?: SvgCanvasData;
  isLoading: boolean;
  error?: string;

  selectedState: SelectedState;

  undoStack: SvgCanvasData[];
  redoStack: SvgCanvasData[];

  assets: SvgAsset[];
  aiHistories: SvgAiHistory[];

  // ========== 新增：子元素编辑模式状态 ==========
  /** 是否处于子元素编辑模式 */
  isSubElementEditMode: boolean;
  /** 当前编辑的节点 ID */
  editingNodeId: string | null;
  /** 当前选中的子元素 ID */
  selectedSubElementId: string | null;

  // actions
  initNewCanvas: (width?: number, height?: number) => void;
  loadCanvas: (canvasId: string) => Promise<void>;
  saveCanvas: (asFile?: boolean) => Promise<void>;
  exportSvgFile: (svgHtml: string) => void;

  addElement: (element: SvgCanvasElement) => void;
  updateElement: (id: string, partial: Partial<SvgCanvasElement>) => void;
  updateElements: (updates: { id: string; partial: Partial<SvgCanvasElement> }[]) => void;
  removeElements: (ids: string | string[]) => void;

  setSelectedIds: (ids: string[]) => void;
  setActiveElement: (id?: string) => void;
  
  // High-level operations
  alignSelectedElements: (type: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom') => void;

  undo: () => void;
  redo: () => void;

  loadAssets: () => Promise<void>;
  addAsset: (asset: Partial<SvgAsset>) => Promise<SvgAsset>;
  deleteAsset: (id: string) => Promise<void>;

  addAiHistory: (history: Partial<SvgAiHistory>) => Promise<void>;
  loadAiHistories: () => Promise<void>;

  addAiSvgToCanvas: (svgContent: string, name?: string, options?: { x?: number; y?: number }) => void;
  // ========== 新增 actions ==========
  /** 进入子元素编辑模式 */
  enterSubElementEditMode: (nodeId: string) => void;
  /** 退出子元素编辑模式 */
  exitSubElementEditMode: () => void;
  /** 选中子元素 */
  selectSubElement: (subElementId: string | null) => void;
  /** 更新子元素属性 */
  updateSubElement: (nodeId: string, subElementId: string, attrs: Partial<SvgSubElement['customAttributes']>) => void;
  /** 解析节点的子元素 */
  parseNodeSubElements: (nodeId: string) => void;
  /** 更新节点动画属性 */
  updateNodeAnimation: (nodeId: string, animation: Partial<SvgAnimationProps>) => void;
  /** 恢复子元素的原始属性 */
  resetSubElementAttribute: (nodeId: string, subElementId: string, attribute: 'fill' | 'stroke' | 'strokeWidth' | 'opacity' | 'textContent' | 'all') => void;
}

const createEmptyCanvas = (width: number, height: number): SvgCanvasData => ({
  canvasId: `canvas_${Date.now()}`,
  width,
  height,
  background: '#ffffff',
  elements: [],
});

const cloneCanvas = (canvas: SvgCanvasData): SvgCanvasData =>
  JSON.parse(JSON.stringify(canvas));

export const useSvgStore = create<SvgStoreState>()(
  devtools((set, get) => ({
    canvas: undefined,
    isLoading: false,
    error: undefined,

    selectedState: {
      selectedIds: [],
    },

    undoStack: [],
    redoStack: [],

    assets: [],
    aiHistories: [],

    // ========== 新增状态初始值 ==========
    isSubElementEditMode: false,
    editingNodeId: null,
    selectedSubElementId: null,

    initNewCanvas: (width = 1200, height = 800) => {
      const newCanvas = createEmptyCanvas(width, height);
      set({
        canvas: newCanvas,
        undoStack: [],
        redoStack: [],
        selectedState: { selectedIds: [] },
        error: undefined,
      });
    },

    loadCanvas: async (canvasId: string) => {
      set({ isLoading: true, error: undefined });
      try {
        const record = await svgAPIService.getCanvasById(canvasId);
        set({
          canvas: record.data,
          undoStack: [],
          redoStack: [],
          selectedState: { selectedIds: [] },
        });
      } catch (error: any) {
        console.error('加载画布失败:', error);
        set({ error: error.message ?? '加载画布失败' });
      } finally {
        set({ isLoading: false });
      }
    },

    saveCanvas: async (asFile = false) => {
      const { canvas } = get();
      if (!canvas) {
        return;
      }
      
      if (asFile) {
        svgAPIService.downloadCanvasAsJson(canvas);
        return;
      }

      set({ isLoading: true, error: undefined });
      try {
        await svgAPIService.saveCanvasSnapshot(canvas.canvasId, canvas);
      } catch (error: any) {
        console.error('保存画布失败:', error);
        set({ error: error.message ?? '保存画布失败' });
      } finally {
        set({ isLoading: false });
      }
    },

    exportSvgFile: (svgHtml: string) => {
      const { canvas } = get();
      if (!canvas) return;
      // 这里的 svgHtml 通常是从 DOM 获取的 innerHTML
      svgAPIService.downloadCanvasAsSvg({
        width: canvas.width,
        height: canvas.height,
        innerHtml: svgHtml
      }, `export_${canvas.canvasId}.svg`);
    },

    addElement: (element: SvgCanvasElement) => {
      // ...existing code...
      const { canvas, undoStack } = get();
      if (!canvas) {
        return;
      }
      const previous = cloneCanvas(canvas);
      const next: SvgCanvasData = {
        ...canvas,
        elements: [...canvas.elements, element],
      };
      set({
        canvas: next,
        undoStack: [...undoStack, previous],
        redoStack: [],
      });
    },

    updateElement: (id: string, partial: Partial<SvgCanvasElement>) => {
      // ...existing code...
      const { canvas, undoStack } = get();
      if (!canvas) {
        return;
      }
      const previous = cloneCanvas(canvas);
      const nextElements = canvas.elements.map((el) =>
        el.id === id ? ({ ...el, ...partial } as SvgCanvasElement) : el
      );
      set({
        canvas: {
          ...canvas,
          elements: nextElements,
        },
        undoStack: [...undoStack, previous],
        redoStack: [],
      });
    },

    updateElements: (updates: { id: string; partial: Partial<SvgCanvasElement> }[]) => {
      const { canvas, undoStack } = get();
      if (!canvas || updates.length === 0) return;

      const previous = cloneCanvas(canvas);
      const updatesMap = new Map(updates.map(u => [u.id, u.partial]));

      const nextElements = canvas.elements.map((el) => {
        const partial = updatesMap.get(el.id);
        if (partial) {
          return { ...el, ...partial } as SvgCanvasElement;
        }
        return el;
      });

      set({
        canvas: { ...canvas, elements: nextElements },
        undoStack: [...undoStack, previous],
        redoStack: [],
      });
    },

    removeElements: (ids: string) => {
      const { canvas, undoStack, selectedState } = get();
      if (!canvas) {
        return;
      }

      // 统一转换为数组
      const idsToRemove = Array.isArray(ids) ? ids : [ids];
      if (idsToRemove.length === 0) return;

      const previous = cloneCanvas(canvas);
      
      // 过滤掉所有在删除列表中的元素
      const nextElements = canvas.elements.filter((el) => !idsToRemove.includes(el.id));
      
      // 更新选中状态：从当前选中列表中移除已删除的 ID
      const nextSelectedIds = selectedState.selectedIds.filter(
        (selectedId) => !idsToRemove.includes(selectedId)
      );

      // 检查当前激活的元素是否被删除
      const isActiveRemoved = selectedState.activeElementId && idsToRemove.includes(selectedState.activeElementId);

      set({
        canvas: {
          ...canvas,
          elements: nextElements,
        },
        selectedState: {
          ...selectedState,
          selectedIds: nextSelectedIds,
          activeElementId: isActiveRemoved
              ? undefined // 如果被删除了，重置 activeElementId
              : selectedState.activeElementId,
        },
        undoStack: [...undoStack, previous],
        redoStack: [],
      });
    },


    setSelectedIds: (ids: string[]) => {
      // ...existing code...
      set((state) => ({
        selectedState: {
          ...state.selectedState,
          selectedIds: ids,
        },
      }));
    },

    setActiveElement: (id?: string) => {
      // ...existing code...
      set((state) => ({
        selectedState: {
          ...state.selectedState,
          activeElementId: id,
          selectedIds: id ? [id] : [],
        },
      }));
    },
    
    // 对齐功能：目前仅支持对齐 x, y。更复杂的对齐需要 BoundingBox（需要 Render 完毕回填或解析 SVG）
    // 这里做最简单的假设：x/y 就是左上角。
    alignSelectedElements: (type) => {
      const { canvas, selectedState, undoStack } = get();
      if (!canvas || selectedState.selectedIds.length < 2) return;

      const selectedElements = canvas.elements.filter(el => selectedState.selectedIds.includes(el.id)) as SvgNodeElement[];
      if (selectedElements.length < 2) return;

      const previous = cloneCanvas(canvas);
      let targetValue = 0;

      // 1. 计算基准值
      if (type === 'left') {
        targetValue = Math.min(...selectedElements.map(el => el.x));
      } else if (type === 'top') {
        targetValue = Math.min(...selectedElements.map(el => el.y));
      } else if (type === 'right') {
         // 注意：没有 width 信息很难做右对齐，这里的简单实现假设对其 X 坐标最大值
         targetValue = Math.max(...selectedElements.map(el => el.x));
      } else if (type === 'bottom') {
         targetValue = Math.max(...selectedElements.map(el => el.y));
      } else if (type === 'center') {
         // 简易版：X 平均值
         const min = Math.min(...selectedElements.map(el => el.x));
         const max = Math.max(...selectedElements.map(el => el.x));
         targetValue = (min + max) / 2;
      } else if (type === 'middle') {
         const min = Math.min(...selectedElements.map(el => el.y));
         const max = Math.max(...selectedElements.map(el => el.y));
         targetValue = (min + max) / 2;
      }

      // 2. 应用
      const nextElements = canvas.elements.map(el => {
        if (!selectedState.selectedIds.includes(el.id) || el.type !== 'svg') return el;
        const node = el as SvgNodeElement;
        
        if (type === 'left' || type === 'right' || type === 'center') {
          return { ...node, x: targetValue };
        } else {
          return { ...node, y: targetValue };
        }
      });

      set({
        canvas: { ...canvas, elements: nextElements },
        undoStack: [...undoStack, previous],
        redoStack: [],
      });
    },

    undo: () => {
       // ...existing code...
      const { undoStack, redoStack, canvas } = get();
      if (!canvas || undoStack.length === 0) {
        return;
      }
      const previous = undoStack[undoStack.length - 1];
      const newUndoStack = undoStack.slice(0, undoStack.length - 1);
      set({
        canvas: previous,
        undoStack: newUndoStack,
        redoStack: [...redoStack, cloneCanvas(canvas)],
      });
    },

    redo: () => {
       // ...existing code...
      const { undoStack, redoStack, canvas } = get();
      if (!canvas || redoStack.length === 0) {
        return;
      }
      const next = redoStack[redoStack.length - 1];
      const newRedoStack = redoStack.slice(0, redoStack.length - 1);
      set({
        canvas: next,
        undoStack: [...undoStack, cloneCanvas(canvas)],
        redoStack: newRedoStack,
      });
    },

    loadAssets: async () => {
       // ...existing code...
      try {
        const assets = await svgAPIService.getAssets();
        set({ assets });
        // 同步一份到本地缓存，方便离线/后端未完成时使用
        saveLocalAssetsSafely(assets);
      } catch (error: any) {
        console.error('加载 SVG 素材库失败:', error);
        set({ error: error.message ?? '加载 SVG 素材库失败' });
        // 回退到本地缓存
        const localAssets = loadLocalAssetsSafely();
        if (localAssets.length > 0) {
          set({ assets: localAssets });
        }
      }
    },
    
    // ...existing code...
    // (Maintain addAsset, deleteAsset, addAiHistory, loadAiHistories)

    addAsset: async (asset: Partial<SvgAsset>) => {
      try {
        const created = await svgAPIService.createAsset(asset);
        set((state) => {
          const nextAssets = [...state.assets, created];
          saveLocalAssetsSafely(nextAssets);
          return {
            assets: nextAssets,
          };
        });
        return created;
      } catch (error: any) {
        console.error('创建 SVG 素材失败:', error);
        // 后端未实现时，退回到本地临时存储
        const fallback: SvgAsset = {
          // 简单生成一个本地 id，避免与后端冲突
          id: `local_${Date.now()}`,
          name: asset.name ?? `SVG_${Date.now()}`,
          svg_code: asset.svg_code ?? '',
          is_public: asset.is_public ?? false,
          from_ai_prompt: asset.from_ai_prompt,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        } as SvgAsset;

        set((state) => {
          const nextAssets = [...state.assets, fallback];
          saveLocalAssetsSafely(nextAssets);
          return {
            assets: nextAssets,
          };
        });

        // 本地模式认为是“成功”的，返回 fallback，避免前端误认为失败
        return fallback;
      }
    },

    deleteAsset: async (id: string) => {
      try {
        await svgAPIService.deleteAsset(id);
        set((state) => {
          const nextAssets = state.assets.filter((asset) => asset.id !== id);
          saveLocalAssetsSafely(nextAssets);
          return {
            assets: nextAssets,
          };
        });
      } catch (error: any) {
        console.error('删除 SVG 素材失败:', error);
        // 后端未实现时，至少保证本地缓存被删掉
        set((state) => {
          const nextAssets = state.assets.filter((asset) => asset.id !== id);
          saveLocalAssetsSafely(nextAssets);
          return {
            assets: nextAssets,
          };
        });
        // 不再抛出，避免前端认为整个操作失败
      }
    },

    addAiHistory: async (history: Partial<SvgAiHistory>) => {
      try {
        const created = await svgAPIService.createAiHistory(history);
        set((state) => ({
          aiHistories: [...state.aiHistories, created],
        }));
      } catch (error: any) {
        console.error('保存 AI 历史失败:', error);
        set({ error: error.message ?? '保存 AI 历史失败' });
      }
    },

    loadAiHistories: async () => {
      try {
        const list = await svgAPIService.getAiHistoryList();
        set({ aiHistories: list });
      } catch (error: any) {
        console.error('获取 AI 历史失败:', error);
        set({ error: error.message ?? '获取 AI 历史失败' });
      }
    },

    addAiSvgToCanvas: (svgContent: string, name?: string, options?: { x?: number; y?: number }) => {
      const { canvas, addElement } = get();
      if (!canvas) {
        return;
      }
      const element: SvgNodeElement = {
        id: `node_${Date.now()}`,
        type: 'svg',
        sourceType: 'ai',
        svgContent,
        originalSvgContent: svgContent, // 保存原始内容
        x: options?.x ?? (canvas.width / 2 - 100),
        y: options?.y ?? (canvas.height / 2 - 100),
        scale: 1,
        rotation: 0,
        opacity: 1,
        name: name ?? 'AI SVG',
      };
      addElement(element);
    },

    // ========== 新增：子元素编辑模式相关 Actions ==========

    /**
     * 进入子元素编辑模式
     */
    enterSubElementEditMode: (nodeId: string) => {
      const { canvas } = get();
      if (!canvas) return;

      const node = canvas.elements.find(el => el.id === nodeId);
      if (!node || node.type !== 'svg') return;

      set({
        isSubElementEditMode: true,
        editingNodeId: nodeId,
        selectedSubElementId: null,
      });
    },

    /**
     * 退出子元素编辑模式
     */
    exitSubElementEditMode: () => {
      set({
        isSubElementEditMode: false,
        editingNodeId: null,
        selectedSubElementId: null,
      });
    },

    /**
     * 选中子元素
     */
    selectSubElement: (subElementId: string | null) => {
      set({ selectedSubElementId: subElementId });
    },

// ...existing code...

    /**
     * 解析节点的子元素结构
     * 同时提取 SVG 中已有的动画属性
     * 并保存原始 SVG 内容用于恢复
     */
    parseNodeSubElements: (nodeId: string) => {
      const { canvas, undoStack } = get();
      if (!canvas) return;

      const nodeIndex = canvas.elements.findIndex(el => el.id === nodeId);
      if (nodeIndex === -1) return;

      const node = canvas.elements[nodeIndex];
      if (node.type !== 'svg') return;

      // 如果已经解析过，就不再重复解析
      if (node.subElements && node.subElements.length > 0) return;

      // 1. 解析子元素结构
      const subElements = parseSvgContent(node.svgContent);

      // 2. 提取已有的动画属性（如果 node.animation 未设置或为 none）
      let animation = node.animation;
      if (!animation || !animation.animationType || animation.animationType === 'none') {
        const extractedAnimation = extractAnimationFromSvg(node.svgContent);
        if (extractedAnimation) {
          animation = extractedAnimation;
        }
      }

      const previous = cloneCanvas(canvas);
      const nextElements = [...canvas.elements];
      nextElements[nodeIndex] = {
        ...node,
        subElements,
        animation: animation || node.animation,
        // 关键：首次解析时保存原始 SVG 内容（如果还没有保存过）
        originalSvgContent: node.originalSvgContent || node.svgContent,
      };

      set({
        canvas: { ...canvas, elements: nextElements },
        undoStack: [...undoStack, previous],
        redoStack: [],
      });
    },

    /**
     * 更新子元素属性
     */
    updateSubElement: (nodeId: string, subElementId: string, attrs: Partial<SvgSubElement['customAttributes']>) => {
      const { canvas, undoStack } = get();
      if (!canvas) return;

      const nodeIndex = canvas.elements.findIndex(el => el.id === nodeId);
      if (nodeIndex === -1) return;

      const node = canvas.elements[nodeIndex];
      if (node.type !== 'svg' || !node.subElements) return;

      const previous = cloneCanvas(canvas);

      // 递归更新子元素
      const updateSubElementInTree = (elements: SvgSubElement[]): SvgSubElement[] => {
        return elements.map(el => {
          if (el.id === subElementId) {
            return {
              ...el,
              customAttributes: { ...el.customAttributes, ...attrs },
            };
          }
          if (el.children) {
            return { ...el, children: updateSubElementInTree(el.children) };
          }
          return el;
        });
      };

      const updatedSubElements = updateSubElementInTree(node.subElements);
      
      // 关键修改：基于原始内容重建 SVG
      const baseContent = node.originalSvgContent || node.svgContent;
      const newSvgContent = rebuildSvgContent(baseContent, updatedSubElements);

      const nextElements = [...canvas.elements];
      nextElements[nodeIndex] = {
        ...node,
        subElements: updatedSubElements,
        svgContent: newSvgContent,
        // 确保 originalSvgContent 始终存在
        originalSvgContent: node.originalSvgContent || node.svgContent,
      };

      set({
        canvas: { ...canvas, elements: nextElements },
        undoStack: [...undoStack, previous],
        redoStack: [],
      });
    },

    /**
     * 恢复子元素的原始属性
     * @param nodeId - 节点 ID
     * @param subElementId - 子元素 ID
     * @param attribute - 要恢复的属性，'all' 表示恢复所有属性
     */
    resetSubElementAttribute: (
      nodeId: string, 
      subElementId: string, 
      attribute: 'fill' | 'stroke' | 'strokeWidth' | 'opacity' | 'textContent' | 'all'
    ) => {
      const { canvas, undoStack } = get();
      if (!canvas) return;

      const nodeIndex = canvas.elements.findIndex(el => el.id === nodeId);
      if (nodeIndex === -1) return;

      const node = canvas.elements[nodeIndex];
      if (node.type !== 'svg' || !node.subElements) return;

      const previous = cloneCanvas(canvas);

      // 递归查找并更新子元素
      const updateSubElements = (elements: SvgSubElement[]): SvgSubElement[] => {
        return elements.map(el => {
          if (el.id === subElementId) {
            // 创建新的 customAttributes 对象
            const newCustomAttributes = { ...el.customAttributes };
            
            if (attribute === 'all') {
              // 恢复所有属性 - 返回空的 customAttributes
              return {
                ...el,
                customAttributes: {},
              };
            }
            
            // 根据属性类型删除对应的自定义值
            switch (attribute) {
              case 'fill':
                delete newCustomAttributes.fill;
                break;
              case 'stroke':
                delete newCustomAttributes.stroke;
                break;
              case 'strokeWidth':
                delete newCustomAttributes.strokeWidth;
                break;
              case 'opacity':
                delete newCustomAttributes.opacity;
                break;
              case 'textContent':
                delete newCustomAttributes.textContent;
                break;
            }
            
            return {
              ...el,
              customAttributes: newCustomAttributes,
            };
          }
          
          // 递归处理子元素
          if (el.children && el.children.length > 0) {
            return {
              ...el,
              children: updateSubElements(el.children),
            };
          }
          
          return el;
        });
      };

      // 更新节点的 subElements
      const updatedSubElements = updateSubElements(node.subElements);
      
      // 关键修复：基于原始内容重建 SVG
      const baseContent = node.originalSvgContent || node.svgContent;
      console.log('resetSubElementAttribute - 基于原始内容重建:', {
        hasOriginal: !!node.originalSvgContent,
        originalPreview: baseContent.substring(0, 100),
        currentPreview: node.svgContent.substring(0, 100),
      });
      
      const newSvgContent = rebuildSvgContent(baseContent, updatedSubElements);
      console.log('resetSubElementAttribute - 重建后内容:', newSvgContent.substring(0, 100));

      const nextElements = [...canvas.elements];
      nextElements[nodeIndex] = {
        ...node,
        subElements: updatedSubElements,
        svgContent: newSvgContent,
      };

      set({
        canvas: { ...canvas, elements: nextElements },
        undoStack: [...undoStack, previous],
        redoStack: [],
      });
    },
    /**
     * 更新节点动画属性
     * 当用户手动修改动画属性时，清除 isOriginalAnimation 标记
     * 但保留 transformOrigin 等已提取的值
     */
    updateNodeAnimation: (nodeId: string, animation: Partial<SvgAnimationProps>) => {
      const { canvas, undoStack } = get();
      if (!canvas) return;

      const nodeIndex = canvas.elements.findIndex(el => el.id === nodeId);
      if (nodeIndex === -1) return;

      const node = canvas.elements[nodeIndex];
      if (node.type !== 'svg') return;

      const previous = cloneCanvas(canvas);
      const nextElements = [...canvas.elements];
      
      // 合并动画属性
      // 保留已有的 transformOrigin（除非用户明确修改）
      // 清除 isOriginalAnimation 标记（因为需要重新生成动画）
      const newAnimation: SvgAnimationProps = {
        ...node.animation,
        ...animation,
        // 只有当用户没有明确设置 transformOrigin 时，才保留原有值
        transformOrigin: animation.transformOrigin !== undefined 
          ? animation.transformOrigin 
          : node.animation?.transformOrigin,
        // 同样保留其他 values
        translateValues: animation.translateValues !== undefined
          ? animation.translateValues
          : node.animation?.translateValues,
        scaleValues: animation.scaleValues !== undefined
          ? animation.scaleValues
          : node.animation?.scaleValues,
        opacityValues: animation.opacityValues !== undefined
          ? animation.opacityValues
          : node.animation?.opacityValues,
        // 用户修改后，不再是原始动画
        isOriginalAnimation: false,
      };

      nextElements[nodeIndex] = {
        ...node,
        animation: newAnimation,
      };

      set({
        canvas: { ...canvas, elements: nextElements },
        undoStack: [...undoStack, previous],
        redoStack: [],
      });
    },
  }))
);